<script setup lang="ts">
import { computed } from 'vue';
import type { TagFilterOptionShape } from '@/composables/useFilterOptions';
import type { TagKind } from '@/composables/api/useTagCatalog';

/**
 * Dropdown row for the tag filter picker. The tag name carries the same
 * kind font colors as the card badges (track = blue, artist = brown,
 * user = green); the header/group tag name trails muted so the grouped list
 * reads as "which group this tag belongs to" at a glance.
 */
const props = defineProps<{
  value: string;
  label: string;
  /** The full option from the registry (name, groupName, kind). */
  option?: TagFilterOptionShape;
  /** Unused. Declared so the filter wrappers' `size` stays out of the DOM. */
  size?: 'sm' | 'md';
}>();

const KIND_CLASSES: Record<TagKind, string> = {
  track: 'text-blue-700 dark:text-blue-400',
  artist: 'text-amber-800 dark:text-amber-500',
  user: 'text-green-700 dark:text-green-400',
};

const nameClass = computed(() => (props.option ? KIND_CLASSES[props.option.kind] : ''));
const groupName = computed(() => props.option?.groupName ?? null);
</script>

<template>
  <span class="flex min-w-0 items-baseline gap-1.5" data-testid="tag-filter-option">
    <span class="truncate font-medium" :class="nameClass">{{ label }}</span>
    <span v-if="groupName" class="text-muted-foreground shrink-0 text-[11px]">
      {{ groupName }}
    </span>
  </span>
</template>
